import api from '@/services/api';
import { get } from 'lodash';

// Types for learning progress API
export interface LearningPath {
    id: string;
    user: number;
    cefr_level: string;
    learning_goal: string;
    adjustment_strategy: string;
    is_active: boolean;
    target_level?: string;
    created_at: string;
    updated_at: string;
}

export interface WeeklyPlan {
    id: string;
    learning_path: string;
    week_number: number;
    goal_summary: string;
    target_skills: string[];
    estimated_hours: number;
    created_at: string;
    completion_percentage?: number;
    is_current_week?: boolean;
}

export interface DailySchedule {
    id: string;
    weekly_plan: string;
    day_of_week: string;
    tasks: string[];
    estimated_duration: string;
    is_completed?: boolean;
    completion_percentage?: number;
    created_at: string;
}

export interface DailySubtopicMapping {
    id: string;
    subtopic_submodule: {
        id: number;
        name: string;
        description: string;
        practice_type: number;
        resource_id: string;
        resource_type: string;
        module: {
            id: number;
            name: string;
        };
    };
    weekly_topic_mapping: {
        id: string;
        topic_submodule: {
            id: number;
            name: string;
        };
    };
    sequence_order: number;
    estimated_minutes: number;
    is_completed: boolean;
    completion_percentage: number;
    question_mappings: Array<{
        id: string;
        submodule_question_id: number;
        sequence_order: number;
        is_completed: boolean;
        user_answer: string | null;
        is_correct: boolean | null;
        score: number | null;
    }>;
    total_questions: number;
    completed_questions: number;
}

export interface SubmoduleQuestionMapping {
    id: string;
    daily_subtopic_mapping: string;
    submodule_question_id: number;
    sequence_order: number;
    is_completed: boolean;
    user_answer?: string;
    is_correct?: boolean;
    attempts_count: number;
    time_spent: string;
    completed_at?: string;
    score?: number;
}

export interface LearningProgressStats {
    total_lessons_completed: number;
    total_lessons: number;
    total_study_time_hours: number;
    overall_progress_percentage: number;
    learning_path: {
        id: string;
        cefr_level: string;
        target_level: string;
        weekly_plans: Array<{
            id: string;
            week_number: number;
            goal_summary: string;
            daily_schedules: Array<{
                id: string;
                day_of_week: string;
                estimated_duration: string;
            }>;
        }>;
    };
    current_streak: number;
    weekly_goal_hours: number;
    weekly_completed_hours: number;
}

export interface TodayScheduleData {
    id: string;
    day_of_week: string;
    tasks: Array<{
        skill: string;
        duration_minutes: number;
        subtopic_id?: string;
    }>;
    estimated_duration: string;
    is_completed: boolean;
    completion_percentage: number;
    subtopic_mappings: Array<{
        id: string;
        subtopic_submodule: {
            id: number;
            name: string;
            description: string;
            practice_type: number;
            resource_id: string;
            resource_type: string;
        };
        sequence_order: number;
        estimated_minutes: number;
        is_completed: boolean;
        completion_percentage: number;
    }>;
    lessons?: Array<{
        id: string;
        ai_lesson?: {
            id: string;
            title: string;
            difficulty: string;
            activities?: Array<{
                id: string;
                activity_type: string;
                content: any;
                difficulty: string;
                estimated_duration: number;
            }>;
        };
        sequence_order: number;
        priority: string;
        is_started: boolean;
        is_completed: boolean;
        completion_percentage: number;
        estimated_duration: string;
        score?: number;
    }>;
    schedule_date?: string;
    learning_mode?: string;
    target_skills?: string[];
}

export interface ConversationSessionData {
    id: string;
    session_type: string;
    status: string;
    created_at: string;
    lesson_content?: {
        subtopics: Array<{
            id: string;
            name: string;
            module_type: string;
            questions: Array<{
                id: number;
                question_text: string;
                question_type: string;
            }>;
        }>;
    };
}

// New consolidated dashboard data interface
export interface ConsolidatedDashboardData {
    userInfo: {
        id: number;
        name: string;
    };
    activeLearningPath: LearningPath & {
        weekly_plans: WeeklyPlan[];
        skill_mappings: Array<{
            id: string;
            skill_name: string;
            skill_category: string;
            current_proficiency: number;
            target_proficiency: number;
            progress_percentage: number;
        }>;
        syllabus_mappings: Array<{
            id: string;
            module_name: string;
            module_description: string;
            is_completed: boolean;
        }>;
        content_suggestions: Array<{
            id: string;
            skill: string;
            content_type: string;
            difficulty: string;
            priority_score: number;
        }>;
    };
    currentWeekPlan: WeeklyPlan;
    todaySchedule: TodayScheduleData | null;
    availableSchedules: TodayScheduleData[];
    weeklyPlans: WeeklyPlan[];
    dashboardStats: LearningProgressStats;
    skillProgress: Array<{
        id: string;
        skill_name: string;
        skill_category: string;
        current_proficiency: number;
        target_proficiency: number;
        progress_percentage: number;
    }>;
    recentActivities: Array<{
        id: string;
        module_name: string;
        activity_type: string;
        activity_data: any;
        timestamp: string;
    }>;
    streakInfo: {
        id: number;
        current_streak: number;
        longest_streak: number;
        last_activity_date: string;
        weekly_goal_hours: number;
        weekly_completed_hours: number;
    };
    recommendations: Array<{
        id: string;
        skill: string;
        content_type: string;
        difficulty: string;
        priority_score: number;
    }>;
}

const baseURL = '/progress';
const conversationBaseURL = '/conversation';

class LearningProgressService {
    getDailyScheduleWithSubtopics = async (scheduleId: string): Promise<DailySubtopicMapping[]> => {
        try {
            const response = await api.get(`${baseURL}/daily-schedules/${scheduleId}/with-subtopics/`);
            const subtopicMappings = get(response.data, 'subtopic_mappings', []);

            return await Promise.all(
                subtopicMappings.map(async (mapping: any) => {
                    // Get detailed subtopic data with questions
                    try {
                        const detailResponse = await api.get(
                            `${baseURL}/daily-subtopics/${mapping.id}/with-questions/`,
                        );
                        return {
                            id: detailResponse.data.id,
                            subtopic_submodule: detailResponse.data.subtopic_submodule,
                            weekly_topic_mapping: detailResponse.data.weekly_topic_mapping,
                            sequence_order: detailResponse.data.sequence_order,
                            estimated_minutes: detailResponse.data.estimated_minutes,
                            is_completed: detailResponse.data.is_completed,
                            completion_percentage: detailResponse.data.completion_percentage,
                            question_mappings: detailResponse.data.question_mappings || [],
                            total_questions: detailResponse.data.total_questions || 0,
                            completed_questions: detailResponse.data.completed_questions || 0,
                        };
                    } catch (error) {
                        console.error(`Failed to fetch subtopic ${mapping.id} details:`, error);
                        return mapping; // Return basic mapping if detailed fetch fails
                    }
                }),
            );
        } catch (error) {
            console.error('Failed to fetch daily schedule with subtopics:', error);
            throw error;
        }
    };

    getSubtopicWithQuestions = async (subtopicId: string): Promise<DailySubtopicMapping> => {
        try {
            const response = await api.get(`${baseURL}/daily-subtopics/${subtopicId}/with-questions/`);

            return {
                id: response.data.id,
                subtopic_submodule: response.data.subtopic_submodule,
                weekly_topic_mapping: response.data.weekly_topic_mapping,
                sequence_order: response.data.sequence_order,
                estimated_minutes: response.data.estimated_minutes,
                is_completed: response.data.is_completed,
                completion_percentage: response.data.completion_percentage,
                question_mappings: response.data.question_mappings || [],
                total_questions: response.data.total_questions || 0,
                completed_questions: response.data.completed_questions || 0,
            };
        } catch (error) {
            console.error('Failed to fetch subtopic with questions:', error);
            throw error;
        }
    };

    completeQuestion = async (
        questionMappingId: string,
        userAnswer: string,
        timeSpentMinutes: number,
    ): Promise<void> => {
        try {
            await api.post(`${baseURL}/submodule-questions/${questionMappingId}/complete/`, {
                user_answer: userAnswer,
                time_spent_minutes: timeSpentMinutes,
            });
        } catch (error) {
            console.error('Failed to complete question:', error);
            throw error;
        }
    };

    updateLessonProgress = async (lessonId: string, progressData: any): Promise<void> => {
        try {
            await api.post(`${baseURL}/schedule-lessons/${lessonId}/update-progress/`, progressData);
        } catch (error) {
            console.error('Failed to update lesson progress:', error);
            throw error;
        }
    };

    startLesson = async (lessonId: string): Promise<void> => {
        try {
            await api.post(`${baseURL}/schedule-lessons/${lessonId}/start/`);
        } catch (error) {
            console.error('Failed to start lesson:', error);
            throw error;
        }
    };

    completeLesson = async (lessonId: string, score?: number): Promise<void> => {
        try {
            await api.post(`${baseURL}/schedule-lessons/${lessonId}/complete/`, {
                score: score,
            });
        } catch (error) {
            console.error('Failed to complete lesson:', error);
            throw error;
        }
    };

    startSchedule = async (scheduleId: string): Promise<void> => {
        try {
            await api.post(`${baseURL}/daily-schedules/${scheduleId}/start/`);
        } catch (error) {
            console.error('Failed to start schedule:', error);
            throw error;
        }
    };

    completeSchedule = async (scheduleId: string): Promise<void> => {
        try {
            await api.post(`${baseURL}/daily-schedules/${scheduleId}/complete/`);
        } catch (error) {
            console.error('Failed to complete schedule:', error);
            throw error;
        }
    };

    getTodayLessons = async (): Promise<any[]> => {
        try {
            // Get today's schedule first
            const todaySchedule = await this.getTodaySchedule();
            if (!todaySchedule) {
                return [];
            }

            // If the schedule has lessons, return them
            if (todaySchedule.lessons && todaySchedule.lessons.length > 0) {
                return todaySchedule.lessons;
            }

            // Otherwise get lessons by schedule ID
            const response = await api.get(`${baseURL}/daily-schedules/${todaySchedule.id}/with-lessons/`);
            return get(response.data, 'lessons', []);
        } catch (error) {
            console.error('Failed to fetch today lessons:', error);
            return [];
        }
    };

    getTodaySchedule = async (): Promise<TodayScheduleData | null> => {
        try {
            const response = await api.get(`${baseURL}/daily-schedules/today/`);
            return this.transformScheduleData(response.data);
        } catch (error) {
            console.error('Failed to fetch today schedule:', error);
            return null;
        }
    };

    getAvailableSchedules = async (): Promise<TodayScheduleData[]> => {
        try {
            const response = await api.get(`${baseURL}/daily-schedules/available/`);
            // Backend now returns a single schedule object instead of array
            if (response.data && !Array.isArray(response.data)) {
                return [this.transformScheduleData(response.data)];
            }
            // Fallback for array response (backward compatibility)
            return Array.isArray(response.data)
                ? response.data.map((schedule: any) => this.transformScheduleData(schedule))
                : [];
        } catch (error) {
            console.error('Failed to fetch available schedules:', error);
            return [];
        }
    };

    getAvailableSchedule = async (): Promise<TodayScheduleData | null> => {
        try {
            const response = await api.get(`${baseURL}/daily-schedules/available/`);
            // Backend returns a single schedule object
            if (response.data && !Array.isArray(response.data)) {
                return this.transformScheduleData(response.data);
            }
            return null;
        } catch (error) {
            console.error('Failed to fetch available schedule:', error);
            return null;
        }
    };

    getScheduleLessons = async (scheduleId: string): Promise<any[]> => {
        try {
            const response = await api.get(`${baseURL}/daily-schedules/${scheduleId}/with-lessons/`);
            return get(response.data, 'lessons', []);
        } catch (error) {
            console.error('Failed to fetch schedule lessons:', error);
            return [];
        }
    };

    getAvailableSchedulesWithLessons = async (): Promise<TodayScheduleData[]> => {
        try {
            const schedules = await this.getAvailableSchedules();

            // Enhance each schedule with lesson data if not already present
            const enhancedSchedules = await Promise.all(
                schedules.map(async (schedule) => {
                    if (!schedule.lessons || schedule.lessons.length === 0) {
                        try {
                            const lessons = await this.getScheduleLessons(schedule.id);
                            return {
                                ...schedule,
                                lessons: lessons,
                            };
                        } catch (error) {
                            console.error(`Failed to get lessons for schedule ${schedule.id}:`, error);
                            return schedule;
                        }
                    }
                    return schedule;
                }),
            );

            return enhancedSchedules;
        } catch (error) {
            console.error('Failed to fetch available schedules with lessons:', error);
            return [];
        }
    };

    getScheduleWithFullDetails = async (scheduleId: string): Promise<TodayScheduleData | null> => {
        try {
            // Use the optimized full-details endpoint that includes activities
            const response = await api.get(`${baseURL}/daily-schedules/${scheduleId}/full-details/`);
            const scheduleData = this.transformScheduleData(response.data);

            // The backend should now include activities in the lesson data
            // No need for additional API calls per lesson
            return scheduleData;
        } catch (error) {
            console.error('Failed to fetch schedule with full details:', error);
            return null;
        }
    };

    getAvailableSchedulesWithActivities = async (): Promise<TodayScheduleData[]> => {
        try {
            // The optimized backend now returns all data in a single call
            // No need for additional API calls per schedule
            const schedules = await this.getAvailableSchedules();

            // Backend now includes lessons and activities data, so we can return directly
            return schedules;
        } catch (error) {
            console.error('Failed to fetch available schedules with activities:', error);
            return [];
        }
    };

    getDashboardStats = async (): Promise<LearningProgressStats> => {
        try {
            const response = await api.get(`${baseURL}/dashboard/stats/`);
            return response.data;
        } catch (error) {
            console.error('Failed to fetch dashboard stats:', error);
            throw error;
        }
    };

    getCurrentWeekData = async (): Promise<any> => {
        try {
            const response = await api.get(`${baseURL}/current-week/`);
            return response.data;
        } catch (error) {
            console.error('Failed to fetch current week data:', error);
            throw error;
        }
    };

    createConversationFromSchedule = async (
        scheduleId: string,
        sessionConfig: any,
    ): Promise<ConversationSessionData> => {
        try {
            // Validate schedule ID
            if (!this.isValidUUID(scheduleId)) {
                throw new Error('Invalid schedule ID format');
            }

            // Get the daily schedule with lessons first
            const scheduleResponse = await api.get(`${baseURL}/daily-schedules/${scheduleId}/full-details/`);
            const scheduleData = scheduleResponse.data;

            // Extract lesson content for conversation
            const lessonContent = {
                schedule_id: scheduleId,
                lessons: (scheduleData.lessons || []).map((lesson: any) => ({
                    id: lesson.id,
                    ai_lesson_id: lesson.ai_lesson?.id,
                    title: lesson.ai_lesson?.title || 'Lesson',
                    difficulty: lesson.ai_lesson?.difficulty || 'medium',
                    estimated_duration: lesson.estimated_duration,
                    sequence_order: lesson.sequence_order,
                })),
                subtopics: (scheduleData.subtopic_mappings || []).map((subtopic: any) => ({
                    id: subtopic.id,
                    name: subtopic.subtopic_submodule?.name || 'Subtopic',
                    resource_type: subtopic.subtopic_submodule?.resource_type || 'general',
                    estimated_minutes: subtopic.estimated_minutes,
                    sequence_order: subtopic.sequence_order,
                })),
            };

            // Create conversation session via conversation service
            const response = await api.post(`${conversationBaseURL}/sessions/`, {
                session_type: 'daily_lesson',
                daily_schedule_id: scheduleId,
                conversation_settings: sessionConfig.conversation_settings || {},
                avatar_settings: sessionConfig.avatar_settings || {},
                lesson_content: lessonContent,
            });

            return {
                id: response.data.id,
                session_type: response.data.session_type,
                status: response.data.status,
                created_at: response.data.created_at,
                lesson_content: response.data.lesson_content,
            };
        } catch (error) {
            console.error('Failed to create conversation from schedule:', error);
            throw error;
        }
    };

    private transformScheduleData = (scheduleData: any): TodayScheduleData => {
        const lessons = get(scheduleData, 'lessons', []);
        const subtopicMappings = get(scheduleData, 'subtopic_mappings', []);

        // Calculate completion based on lessons and subtopics
        const totalItems = lessons.length + subtopicMappings.length;
        const completedItems =
            lessons.filter((lesson: any) => lesson.is_completed).length +
            subtopicMappings.filter((subtopic: any) => subtopic.is_completed).length;

        const completionPercentage = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

        return {
            id: scheduleData.id,
            day_of_week: scheduleData.day_of_week,
            tasks: scheduleData.tasks || [],
            estimated_duration: scheduleData.estimated_duration,
            is_completed: scheduleData.is_completed || completionPercentage >= 100,
            completion_percentage: completionPercentage,
            subtopic_mappings: subtopicMappings,
            lessons: lessons,
            schedule_date: scheduleData.schedule_date,
            learning_mode: scheduleData.learning_mode,
            target_skills: scheduleData.target_skills || [],
        };
    };

    private isValidUUID = (uuid: string): boolean => {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        return uuidRegex.test(String(uuid));
    };
}

export const learningProgressService = new LearningProgressService();
